import { FunctionComponent, useCallback, useEffect, useState, useContext, useMemo, useRef } from "react"
import { useHistory } from "react-router-dom"
import Logger from "classes/Logger"
import SummaryOptions from "components/Summary/SummaryOptions"
import IPage from "interfaces/IPage"
import UseGetMenuUser from "overrides/theme/wom/base/hooks/UseGetMenuUser"
import UseGroupedOrderByCount from "overrides/theme/wom/base/hooks/UseGroupedOrderByCount"
import { ISearchResults } from "interfaces/ISearchResults"
import { AuthContext } from "context/context/AuthContext"
import { ResourceRoleEnum } from "enums/ResourceRoleEnum"
import SelectBox from "components/Commons/SelectBox/SelectBox"
import UseFilterShippingType from "hooks/UseFilterShippingType"
import { ContentSelectFilter } from "components/Commons/SelectBox/SelectBox.styled"
import Multiclient from "components/Core/Multiclient"
import { getSummaryMenuOrderSellerSTT } from "enums/OptionsCardUserSellerSTT"
import useEnv from "hooks/core/useEnv"
import useUpdateMenuCounts from "hooks/useUpdateMenuCounts"
import { IconSpinner } from "theme/common/Icon.styled"

const DashBordScreen: FunctionComponent<IPage> = (props: any) => {

  const {
    state: { user },
  } = useContext(AuthContext)

  const monitorUser = () => {
    return user?.role === ResourceRoleEnum["webapp-monitor-source"]
  }

  const { name } = props
  const history = useHistory()

  const [shippingTypeOption, setShippingTypeOption] = useState("")

  const handleChangeShippingTypeOptions = (value: string) => setShippingTypeOption(value)

  const {
    optionsComboBox,
    canIuseFilterShippingType
  } = UseFilterShippingType({
    shippingTypeOption
  })

  const [reload, setReload] = useState(false)

  const [initState, setInitState] = useState(true)

  const { getMenuSummaryOrder } = UseGetMenuUser()

  const { groupedOrder } = UseGroupedOrderByCount(shippingTypeOption)

  const isSellerSourceBoSstt = useMemo(() => {
    if (!user?.role) return false
    const allowedRoles = [
      ResourceRoleEnum["webapp-seller-source-bo-sstt"]
    ]
    return allowedRoles.includes(user.role as ResourceRoleEnum)
  }, [user?.role])

  const getChannels = useCallback(() => {
    if (!user?.groups) return [""]
    
    const filteredGroups = user.groups
      .filter((group: any) => group.type === "Channel")
      .map((group: any) => group.id)
    
    return filteredGroups.length > 0 ? filteredGroups : [""]
  }, [user?.groups])

  const getSources = useCallback(() => {
    if (!user?.currentSources) return [""]
    return user.currentSources
  }, [user?.currentSources])

  const { sg } = useEnv()

  const summaryMenuOrder = useMemo(() => {
    if (isSellerSourceBoSstt) {
      return getSummaryMenuOrderSellerSTT({
        shippingTypeOption: shippingTypeOption ? [shippingTypeOption] : [],
        user,
        getSources,
        getChannels,
        sg
      })
    }
    return getMenuSummaryOrder(shippingTypeOption)
  }, [isSellerSourceBoSstt, user, getSources, getChannels, getMenuSummaryOrder, shippingTypeOption, sg])

  // Hook para actualizar los conteos cuando es seller source bo sstt
  const { menuItems: menuItemsWithCounts, loading: loadingCounts, updateCounts } = useUpdateMenuCounts()
  
  // Ref para rastrear el último summaryMenuOrder procesado y evitar llamadas duplicadas
  const lastProcessedMenuRef = useRef<string>("")
  const isProcessingRef = useRef<boolean>(false)

  // Actualizar conteos cuando cambie el summaryMenuOrder y sea seller source bo sstt
  useEffect(() => {
    if (!isSellerSourceBoSstt || !summaryMenuOrder || summaryMenuOrder.length === 0) {
      return
    }

    // Crear una clave única basada en el contenido del menú para comparar
    const menuKey = JSON.stringify(
      summaryMenuOrder.map(item => ({
        path: item.path,
        status: item.status,
        body: item.body
      }))
    )

    // Evitar llamadas duplicadas si:
    // 1. Ya se está procesando
    // 2. El menú no ha cambiado realmente
    if (isProcessingRef.current || lastProcessedMenuRef.current === menuKey) {
      return
    }

    // Marcar como procesando y actualizar la referencia
    isProcessingRef.current = true
    lastProcessedMenuRef.current = menuKey

    updateCounts(summaryMenuOrder).finally(() => {
      isProcessingRef.current = false
    })
  }, [isSellerSourceBoSstt, summaryMenuOrder, updateCounts])

  // Usar menuItems con conteos actualizados si es seller source bo sstt, sino usar summaryMenuOrder original
  const finalSummaryMenuOrder = isSellerSourceBoSstt ? menuItemsWithCounts : summaryMenuOrder

  useEffect(() => {
    const st = setTimeout(() => {
      if(monitorUser() && !isSellerSourceBoSstt) {
        groupedOrder()
        setReload(!reload)
      }
    }, 1000 * 60)

    return () => clearTimeout(st)
    //eslint-disable-next-line
  }, [reload, isSellerSourceBoSstt])

  useEffect(() => {
    Logger.info(`Loading ${name}`)
  }, [name])

  const handleClickSearch = useCallback(
    ({ query, optionFilter }: ISearchResults) => {
      if (query) history.push(`/search/${query}/${optionFilter}`)
    },
    [history],
  )

  useEffect( () => {
    if(canIuseFilterShippingType && initState) setInitState(false);

    if(!initState && !isSellerSourceBoSstt) groupedOrder()
    //eslint-disable-next-line
  }, [canIuseFilterShippingType, shippingTypeOption, isSellerSourceBoSstt])

  return (
    <>
      {!monitorUser() && (
        <Multiclient
          path_override="overrides/theme/wom/base/components/SearchForm/SearchForm"
          onClickSearch={handleClickSearch}
        />
      )}
      {canIuseFilterShippingType && (
        <ContentSelectFilter>
            <SelectBox
              options={optionsComboBox}
              onchange={handleChangeShippingTypeOptions}
              defaultValue={""}
            />
        </ContentSelectFilter>
      )}
      {isSellerSourceBoSstt && loadingCounts && (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          color: '#666',
          fontSize: '26px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '15px'
        }}>
          <IconSpinner variant="big" />
          <div>Cargando información de las bandejas...</div>
        </div>
      )}
      {finalSummaryMenuOrder && finalSummaryMenuOrder.length > 0 && !(isSellerSourceBoSstt && loadingCounts) && (
        <SummaryOptions summaryOptions={finalSummaryMenuOrder} />
      )}
    </>
  )
}

export default DashBordScreen